import {
  DeserializedRecord,
  deserializeItem,
  deserializeManyRelationship,
  Deserializer,
  deserializeSingleRelationship,
  JSONAPIContent,
  JSONAPIManyRelationship,
  JSONAPIRecord,
  JSONAPISingleRelationship
} from "./Helpers";
import {deserializePhysician, Physician} from "./Physician";
import {deserializePatient, Patient} from "./Patient";

interface Feedback extends DeserializedRecord<"feedback"> {
  rating: number;
  remark: string;
}

interface ActiveParticipants {
  sid: string;
  identity: string;
  state: string;
}

interface Participant extends DeserializedRecord<"participant"> {
  email: string;
  liveKitName?: string;
  liveKitIdentity: string;
  feedback?: Feedback;
  participantName: string;
  subject: Patient | Physician;
  subjectId: number;
  subjectType: "Patient" | "Physician";
  _destroy?: boolean;
}

interface VideoConsultation extends DeserializedRecord<"video_consultation"> {
  liveKitRoomId: string;
  title: string;
  remark: string;
  callBeginAt?: Date;
  channelToken: string;
  deleteDisabled: boolean;
  editDisabled: boolean;
  isCompleted: boolean;
  participantNames: Array<string>;
  participants: Array<Participant>;
  activeParticipants: Array<ActiveParticipants>
  plannedBeginAt: Date;
  plannedDurationInMinutes: number;
  physician?: Physician;
  status: "planned" | "cancelled" | "running" | "completed";
}

interface ParticipantData extends JSONAPIRecord {
  relationships: {
    video_consultation_feedback: JSONAPISingleRelationship;
    subject: JSONAPISingleRelationship;
  }
}

const deserializeParticipant: Deserializer<Participant, ParticipantData> = (data, included) => {
  const feedback = deserializeSingleRelationship<Feedback>(data.relationships.video_consultation_feedback, included);
  const participant = deserializeItem<Participant>(data);
  let subject = null;

  if (data.relationships.subject) {
    if (participant.subjectType === "Patient") {
      subject = deserializeSingleRelationship<Patient>(data.relationships.subject, included, deserializePatient);
    } else {
      subject = deserializeSingleRelationship<Physician>(data.relationships.subject, included, deserializePhysician);
    }
  }

  return {
    ...participant,
    subject,
    feedback
  };
};

interface VideoConsultationData extends JSONAPIRecord {
  attributes: JSONAPIRecord["attributes"] & {
    status: VideoConsultation["status"],
    active_participants: Array<JSONAPIContent<JSONAPIRecord>>
  }
  relationships: {
    video_consultation_participants: JSONAPIManyRelationship;
    physician: JSONAPISingleRelationship;
  }
}

const deserializeVideoConsultation: Deserializer<VideoConsultation, VideoConsultationData> = (data, included) => {
  let participants = [];
  let activeParticipants = [];
  let physician = null;

  if (data.relationships?.physician) {
    physician = deserializeSingleRelationship(data.relationships.physician, included, deserializePhysician);
  }

  if (data.relationships?.video_consultation_participants) {
    participants = deserializeManyRelationship(data.relationships.video_consultation_participants, included, deserializeParticipant);
  }

  if (data.attributes?.active_participants?.length > 0) {
    activeParticipants = data.attributes.active_participants.map((ap) => deserializeItem(ap.data));
  }

  return {
    ...deserializeItem(data),
    deleteDisabled: ['running', 'completed'].includes(data.attributes.status),
    editDisabled: ['running', 'cancelled', 'completed'].includes(data.attributes.status),
    isCompleted: ['cancelled', 'completed'].includes(data.attributes.status),
    physician,
    participants,
    activeParticipants
  };
};

export {
  deserializeVideoConsultation,
  Participant,
  VideoConsultation
};
